/**
 * Utility functions for validating and processing AI screenshot results
 */

/**
 * Validate player statistics for consistency and realistic values
 * @param {Array} players - Array of player objects with stats
 * @returns {Object} Validation result with issues array
 */
export const validatePlayerStats = (players) => {
  const issues = [];
  const warnings = [];

  if (!Array.isArray(players) || players.length === 0) {
    return {
      isValid: false,
      issues: ['No player data provided'],
      warnings: []
    };
  }

  players.forEach((player, index) => {
    const { playerName, stats } = player;
    
    // Basic structure validation
    if (!playerName || typeof playerName !== 'string') {
      issues.push(`Player ${index + 1}: Missing or invalid player name`);
    }
    
    if (!stats || typeof stats !== 'object') {
      issues.push(`Player ${index + 1}: Missing stats object`);
      return;
    }

    // Validate individual stats
    const statKeys = ['goals', 'assists', 'passes', 'interceptions', 'saves'];
    statKeys.forEach(statKey => {
      const value = stats[statKey];
      if (typeof value !== 'number' || value < 0) {
        issues.push(`${playerName}: Invalid ${statKey} value (${value})`);
      }
      
      // Warn about unusually high values
      if (value > 50) {
        warnings.push(`${playerName}: Unusually high ${statKey} (${value}) - please verify`);
      }
    });

    // Logical validation
    if (stats.assists > stats.goals + 10) {
      warnings.push(`${playerName}: More assists (${stats.assists}) than expected given goals (${stats.goals})`);
    }

    if (stats.saves > 0 && stats.goals > 10) {
      warnings.push(`${playerName}: High saves (${stats.saves}) and goals (${stats.goals}) - unusual for one player`);
    }

    // Check for completely empty stats
    const totalStats = statKeys.reduce((sum, key) => sum + (stats[key] || 0), 0);
    if (totalStats === 0) {
      warnings.push(`${playerName}: No statistics recorded`);
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
    warnings
  };
};

/**
 * Normalize and clean player names for better matching
 * @param {string} name - Raw player name from AI
 * @returns {string} Cleaned player name
 */
export const normalizePlayerName = (name) => {
  if (!name || typeof name !== 'string') {
    return '';
  }

  return name
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s\-.]/g, '') // Remove special characters except dash and dot
    .substring(0, 50); // Limit length
};

/**
 * Sanitize AI response to prevent XSS and other security issues
 * @param {Object} response - Raw AI response
 * @returns {Object} Sanitized response
 */
export const sanitizeAIResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return { success: false, error: 'Invalid response format' };
  }

  const sanitized = {
    success: Boolean(response.success),
    confidence: Math.min(1, Math.max(0, parseFloat(response.confidence) || 0)),
    players: [],
    extractedScores: null,
    mvpPlayer: null,
    error: null
  };

  // Sanitize error message
  if (response.error && typeof response.error === 'string') {
    sanitized.error = response.error.substring(0, 500); // Limit error message length
  }

  // Sanitize players array
  if (Array.isArray(response.players)) {
    sanitized.players = response.players
      .slice(0, 20) // Limit to 20 players max
      .map(player => {
        if (!player || typeof player !== 'object') {
          return null;
        }

        const sanitizedPlayer = {
          playerName: normalizePlayerName(player.playerName),
          stats: {
            goals: Math.max(0, Math.min(100, parseInt(player.stats?.Goal) || 0)),
            assists: Math.max(0, Math.min(100, parseInt(player.stats?.Assist) || 0)),
            passes: Math.max(0, Math.min(1000, parseInt(player.stats?.Pass) || 0)),
            interceptions: Math.max(0, Math.min(100, parseInt(player.stats?.Interception) || 0)),
            saves: Math.max(0, Math.min(100, parseInt(player.stats?.Save) || 0)),
            score: Math.max(0, Math.min(10000, parseInt(player.stats?.Score) || 0))
          }
        };

        // Only return player if name exists
        return sanitizedPlayer.playerName ? sanitizedPlayer : null;
      })
      .filter(Boolean); // Remove null entries
  }

  // Sanitize extracted scores
  if (response.extractedScores && typeof response.extractedScores === 'object') {
    sanitized.extractedScores = {
      homeScore: Math.max(0, Math.min(100, parseInt(response.extractedScores.homeScore) || 0)),
      awayScore: Math.max(0, Math.min(100, parseInt(response.extractedScores.awayScore) || 0)),
      // Also keep the original format for backwards compatibility
      team1Score: Math.max(0, Math.min(100, parseInt(response.extractedScores.homeScore) || 0)),
      team2Score: Math.max(0, Math.min(100, parseInt(response.extractedScores.awayScore) || 0))
    };
  }

  // Sanitize MVP player
  if (response.mvpPlayer && typeof response.mvpPlayer === 'string') {
    // Sanitize and normalize the MVP player name
    const sanitizedMvp = normalizePlayerName(response.mvpPlayer);
    if (sanitizedMvp && sanitizedMvp.length > 0) {
      sanitized.mvpPlayer = sanitizedMvp;
    }
  }

  return sanitized;
};

/**
 * Check if the AI processing result meets quality thresholds
 * @param {Object} result - AI processing result
 * @returns {Object} Quality assessment
 */
export const assessResultQuality = (result) => {
  const assessment = {
    quality: 'good',
    confidence: result.confidence || 0,
    issues: [],
    recommendations: []
  };

  // Confidence assessment
  if (result.confidence < 0.5) {
    assessment.quality = 'poor';
    assessment.issues.push('Very low confidence score');
    assessment.recommendations.push('Consider retaking the screenshot with better quality');
  } else if (result.confidence < 0.7) {
    assessment.quality = 'fair';
    assessment.issues.push('Low confidence score');
    assessment.recommendations.push('Review extracted data carefully before applying');
  }

  // Player count assessment
  if (!result.players || result.players.length === 0) {
    assessment.quality = 'poor';
    assessment.issues.push('No players detected');
    assessment.recommendations.push('Ensure the screenshot shows a clear statistics table');
  } else if (result.players.length < 4) {
    assessment.quality = 'fair';
    assessment.issues.push('Few players detected');
    assessment.recommendations.push('Check if all players are visible in the screenshot');
  }

  // Data consistency assessment
  const validation = validatePlayerStats(result.players);
  if (!validation.isValid) {
    assessment.quality = 'poor';
    assessment.issues.push(...validation.issues);
  }
  if (validation.warnings.length > 0) {
    assessment.recommendations.push(...validation.warnings);
  }

  return assessment;
};

/**
 * Generate user-friendly error messages for different failure scenarios
 * @param {string} errorType - Type of error
 * @param {Object} details - Additional error details
 * @returns {Object} User-friendly error information
 */
export const generateErrorMessage = (errorType, details = {}) => {
  const errorMessages = {
    'network_error': {
      title: 'Network Error',
      message: 'Unable to connect to the AI processing service. Please check your internet connection and try again.',
      action: 'Retry Upload'
    },
    'file_too_large': {
      title: 'File Too Large',
      message: 'The screenshot file is too large. Please compress it or upload a smaller image.',
      action: 'Choose Different File'
    },
    'invalid_file': {
      title: 'Invalid File',
      message: 'Please upload a valid image file (PNG, JPG, or WebP).',
      action: 'Choose Valid Image'
    },
    'ai_processing_failed': {
      title: 'AI Processing Failed',
      message: 'The AI could not process this screenshot. Try uploading a clearer image with visible player statistics.',
      action: 'Try Different Screenshot'
    },
    'no_data_found': {
      title: 'No Data Found',
      message: 'No player statistics were detected in the screenshot. Ensure the image shows a clear match results table.',
      action: 'Upload Clearer Screenshot'
    },
    'api_error': {
      title: 'Service Error',
      message: 'The AI processing service is temporarily unavailable. Please try again later.',
      action: 'Try Again Later'
    },
    'rate_limit': {
      title: 'Rate Limit Exceeded',
      message: 'Too many requests. Please wait a moment before trying again.',
      action: 'Wait and Retry'
    }
  };

  const errorInfo = errorMessages[errorType] || errorMessages['ai_processing_failed'];
  
  return {
    ...errorInfo,
    details: details.message || '',
    timestamp: new Date().toISOString()
  };
};

